const {
	trackGtm,
	trackGtm4,
	initControllerLink,
	fillWithDomElements,
	setHeightFrom,
	toggleMenuArias,
	getFocusablesFrom,
	disableStaticURL,
} = require('util/functions');
const {
	outClickSubscribe,
	outClickUnSubscribe,
} = require('util/outClickSubscriber');
const asyncImport = require('util/asyncImport').default;
const milestones = [25, 50, 75];

function mileStoneFromPercent(percent) {
	const value = Math.max(
		...milestones
			.map(milestone => ({ diff: percent - milestone, milestone }))
			.filter(milestone => milestone.diff >= 0)
			.map(obj => obj.milestone),
	);
	return milestones.includes(value) ? value : null;
}

const CLASS_VIDEO_CONTAINER = 'basic-video';
const CLASS_VIDEO = 'video';
const CLASS_IMAGE_CONTAINER = 'basic-video--img-container';
const CLASS_START = 'basic-video--start-button';
const CLASS_DOWNLOAD = 'basic-video--download';
const CLASS_LOADING_OVERLAY = 'loading-overlay';
const CLASS_ACTIVE = 'active';

const CLASS_TRANSCRIPT_BUTTON = 'basic-video-transcript--button';
const CLASS_TRANSCRIPT_CONTAINER = 'basic-video-transcript--container';
const CLASS_TRANSCRIPT_CONTAINED = 'basic-video-transcript--contained';

const CLASS_UNFOLDED = 'unfolded';

const SELECTOR_VIDEO_DOWNLOAD = '.basic-video--download';

const selectors = {
	videoContainer: CLASS_VIDEO_CONTAINER,
	childVideo: CLASS_VIDEO,
	image: CLASS_IMAGE_CONTAINER,
	startButton: CLASS_START,
	downloadButton: CLASS_DOWNLOAD,
	transcriptButton: CLASS_TRANSCRIPT_BUTTON,
	transcriptContainer: CLASS_TRANSCRIPT_CONTAINER,
	transcriptContained: CLASS_TRANSCRIPT_CONTAINED,
	loadingOverlay: CLASS_LOADING_OVERLAY,
};

module.exports = {
	async init() {
		fillWithDomElements(this, selectors);
		initControllerLink(this);
		this.startButton?.addEventListener(
			'click',
			() => {
				this.createVideo();
			},
			{ once: true },
		);
		this.watchedSteps = new Set();
		await asyncImport(this, 'throttle');
		//createVideoFunction is necessary if we have multiple dynamic videos(interactive-map), can't remove 'once' from the button event listener because it causes issues for Plyr
		this.createVideoFunction = this.createVideo.bind(this);
		this.analyseBreakPointThrottled = this.throttle(
			this.analyseBreakPoint.bind(this),
			1000,
		);
		if (this.downloadButton) {
			this.downloadButton.addEventListener('click', e => {
				this.handlerDownloadTracking(e.target);
				disableStaticURL(e.target, 'download-disabled');
			});
		}
		if (this.transcriptButton) this.initTranscript();

		this.url = this.root.dataset.id
			? this.root.dataset.id
			: this.root.dataset.videoUrl;

		this.videoEnded = false;

		if (this.root.dataset.video23) {
			this.modifyVideo23Schema();
		}
	},
	modifyVideo23Schema() {
		const jsonScript = this.root.querySelector(
			'script[type="application/ld+json"]',
		);
		const jsonData = JSON.parse(jsonScript.textContent);
		const url = this.root.querySelector('iframe').getAttribute('src');

		jsonData.contentUrl = url;
		jsonScript.textContent = JSON.stringify(jsonData, null, 4);
	},
	createVideo() {
		this.childVideo.__controller.createVideo();
		this.childVideo.__controller.onReady = this.setUpHandlers.bind(this);
		this.loadingOverlay.classList.add(CLASS_ACTIVE);
	},

	handlerDownloadTracking(link) {
		const href = this.root.dataset.id ? this.root.dataset.id : link;

		trackGtm({
			eventCategory: 'outgoing tracking',
			eventAction: 'Download',
			eventLabel: `${this.root.dataset.title} / ${href} `,
		});

		trackGtm4({
			event: 'GA4_event_c_video_download',
			widget_name: this.getParentWidgetName(),
			video_title: this.root.dataset.title
				? this.root.dataset.title
				: 'No title',
			video_url: this.getVideoURL(),
			video_duration: this.getVideoLength() ? this.getVideoLength() : 'unknown',
			video_provider: this.getVideoProvider(),
			visible: 'visible',
		});
	},
	getVideoLength() {
		let totalSeconds;
		const videoLenght = this.root.getAttribute('data-length');
		if (videoLenght) {
			const minutes = Number(videoLenght.split(':')[0]);
			const seconds = Number(videoLenght.split(':').pop());
			totalSeconds = minutes * 60 + seconds;
		} else {
			totalSeconds = this.root.getAttribute('data-length');
		}
		return totalSeconds;
	},
	handlerVideoReady() {
		if (/iPad|iPhone|iPod/.test(navigator.userAgent) || window.MSStream) {
			this.removeImage();
		}
		this.childVideo.__controller.play();
	},

	getVideoURL() {
		let videoURL;
		if (this.root.dataset.video23) {
			videoURL = this.root.querySelector('iframe').src;
		} else {
			videoURL = this.root.getAttribute('data-id')
				? (videoURL = `youtube.com/watch?v=${this.root.getAttribute(
						'data-id',
				  )}`)
				: (videoURL = this.root
						.querySelector(SELECTOR_VIDEO_DOWNLOAD)
						.getAttribute('href'));
		}
		return videoURL;
	},

	getVideoProvider() {
		let videoProvider;
		if (this.root.dataset.video23) {
			videoProvider = 'video23';
		} else {
			videoProvider = this.root.getAttribute('data-id')
				? (videoProvider = 'Youtube')
				: (videoProvider = 'PMI Resource');
		}
		return videoProvider;
	},

	calculateVideoPercent(currentTime, videoDuration) {
		return Math.round(
			(Math.round(currentTime) * 100) / Math.round(videoDuration),
		);
	},

	getParentWidgetName() {
		let parentWidgetName;
		return this.root.dataset.parent
			? (parentWidgetName = this.root.dataset.parent)
			: (parentWidgetName = '');
	},

	handlerPlay() {
		const videoPercent = this.calculateVideoPercent(
			this.childVideo.__controller.video.currentTime,
			this.childVideo.__controller.video.duration,
		);

		this.loadingOverlay.classList.remove(CLASS_ACTIVE);
		this.videoEnded = false;
		trackGtm4({
			event: 'GA4_event_c_video_start',
			video_title: this.startButton.innerText,
			video_url: this.getVideoURL(),
			video_current_time: `${Math.round(
				this.childVideo.__controller.video.currentTime,
			)}`,
			percent: `${videoPercent}%`,
			widget_name: this.getParentWidgetName(),
			video_duration: `${Math.round(
				this.childVideo.__controller.video.duration,
			)}`,
			video_provider: this.getVideoProvider(),
		});
		trackGtm({
			eventCategory: 'video',
			eventAction: 'play',
			eventLabel: `${this.root.dataset.title} / ${this.url}`,
		});

		if (!(/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream)) {
			setTimeout(() => {
				this.removeImage();
			}, 200);
		}

		this.managePoster();
	},

	managePoster() {
		if (this.childVideo.__controller.video.currentTime > 3) {
			this.childVideo.__controller.video.elements.poster.style.display =
				'block';
		} else {
			this.childVideo.__controller.video.elements.poster.style.display = 'none';
		}
	},

	handlerPause() {
		const videoPercent = this.calculateVideoPercent(
			this.childVideo.__controller.video.currentTime,
			this.childVideo.__controller.video.duration,
		);

		setTimeout(() => {
			if (!this.videoEnded) {
				trackGtm4({
					event: 'GA4_event_c_video_pause',
					video_title: this.startButton.innerText,
					video_url: this.getVideoURL(),
					video_current_time: `${Math.round(
						this.childVideo.__controller.video.currentTime,
					)}`,
					percent: `${videoPercent}%`,
					widget_name: this.getParentWidgetName(),
					video_duration: `${Math.round(
						this.childVideo.__controller.video.duration,
					)}`,
					video_provider: this.getVideoProvider(),
				});
				trackGtm({
					eventCategory: 'video',
					eventAction: 'pause',
					eventLabel: `${this.root.dataset.title} / ${this.url}`,
				});
			}
		}, 0);

		this.childVideo.__controller.video.elements.poster.style.display = 'none';
	},

	handlerEnded() {
		this.videoEnded = true;
		this.watchedSteps = new Set();
		trackGtm4({
			event: 'GA4_event_c_video_complete',
			video_title: this.startButton.innerText,
			video_url: this.getVideoURL(),
			video_current_time: `${Math.round(
				this.childVideo.__controller.video.currentTime,
			)}`,
			percent: '100%',
			widget_name: this.getParentWidgetName(),
			video_duration: `${Math.round(
				this.childVideo.__controller.video.duration,
			)}`,
			video_provider: this.getVideoProvider(),
		});
		trackGtm({
			eventCategory: 'video',
			eventAction: '100%',
			eventLabel: `${this.root.dataset.title} / ${this.url}`,
		});
	},
	getProgressTimeInPercent() {
		return Math.floor(
			(this.childVideo.__controller.video.currentTime /
				this.childVideo.__controller.video.duration) *
				100,
		);
	},
	analyseBreakPoint() {
		const currentStep = mileStoneFromPercent(this.getProgressTimeInPercent());
		if (
			currentStep &&
			!this.watchedSteps.has(currentStep) &&
			!this.videoEnded
		) {
			this.watchedSteps.add(currentStep);
			trackGtm4({
				event: 'GA4_event_c_video_progress',
				video_title: this.startButton.innerText,
				video_url: this.getVideoURL(),
				video_current_time: `${Math.round(
					this.childVideo.__controller.video.currentTime,
				)}`,
				percent: `${currentStep}%`,
				widget_name: this.getParentWidgetName(),
				video_duration: `${Math.round(
					this.childVideo.__controller.video.duration,
				)}`,
				video_provider: this.getVideoProvider(),
			});
			trackGtm({
				eventCategory: 'video',
				eventAction: `${currentStep}%`,
				eventLabel: `${this.root.dataset.title} / ${this.url}`,
			});
		}
	},

	handlerTimeUpdate() {
		this.analyseBreakPointThrottled();

		if (
			this.childVideo.__controller.video.currentTime > 2.8 &&
			this.childVideo.__controller.video.currentTime < 3
		) {
			this.childVideo.__controller.video.elements.poster.style.display =
				'block';
		}
	},
	handleSeeked() {
		const videoPercent = this.calculateVideoPercent(
			this.childVideo.__controller.video.currentTime,
			this.childVideo.__controller.video.duration,
		);
		trackGtm4({
			event: 'GA4_event_c_video_seek',
			video_title: this.startButton.innerText,
			video_url: this.getVideoURL(),
			video_current_time: `${Math.round(
				this.childVideo.__controller.video.currentTime,
			)}`,
			percent: `${videoPercent}%`,
			widget_name: this.getParentWidgetName(),
			video_duration: `${Math.round(
				this.childVideo.__controller.video.duration,
			)}`,
			video_provider: this.getVideoProvider(),
		});
	},
	setUpHandlers() {
		this.childVideo.__controller.on('ready', () => this.handlerVideoReady());
		this.childVideo.__controller.on('play', () => this.handlerPlay());
		this.childVideo.__controller.on('pause', () => this.handlerPause());
		this.childVideo.__controller.on('ended', () => this.handlerEnded());
		this.childVideo.__controller.on('seeked', () => this.handleSeeked());
		this.childVideo.__controller.on('timeupdate', () =>
			this.handlerTimeUpdate(),
		);
		//Solves the firefox issue where the controls don't show up on hover
		this.childVideo.__controller.on('mouseover', function () {
			if (!this.classList.contains('plyr--paused'))
				this.classList.remove('plyr--hide-controls');
		});
	},

	removeImage() {
		this.image.style.display = 'none';
	},

	// transcript

	initTranscript() {
		this.isTranscriptUnfolded = false;
		this.transcriptContentFocusables = getFocusablesFrom(
			this.transcriptContained,
		);
		this.transcriptButton.addEventListener('click', () => {
			this.toggleTranscript(!this.isTranscriptUnfolded, true);
		});
		this.toggleTranscript(false, false);
	},
	toggleTranscript(isUnfolded, buttonStatus) {
		this.showLabel = this.transcriptButton.dataset.showLabel;
		this.hideLabel = this.transcriptButton.dataset.hideLabel;
		this.isTranscriptUnfolded = isUnfolded;
		this.transcriptButton.classList.toggle(CLASS_UNFOLDED, isUnfolded);
		this.transcriptContainer.classList.toggle(CLASS_UNFOLDED, isUnfolded);
		this.transcriptContainer.setAttribute('aria-hidden', isUnfolded);
		toggleMenuArias(
			this.transcriptButton,
			this.transcriptContentFocusables,
			isUnfolded,
		);
		// editing directly the node allows to avoid
		// after's transition to be reinitialised on firefox
		this.transcriptButton.childNodes[0].nodeValue = isUnfolded
			? this.hideLabel
			: this.showLabel;
		if (isUnfolded) {
			outClickSubscribe(this.root, () => this.toggleTranscript(false, true));
			setHeightFrom(this.transcriptContainer, this.transcriptContained);
			this.transcriptTrackGa4('visible');
		} else {
			this.transcriptContainer.style.height = 0;
			outClickUnSubscribe(this.root);
			if (buttonStatus) {
				this.transcriptTrackGa4('closed');
			}
		}
	},
	transcriptTrackGa4(status) {
		trackGtm4({
			event: 'GA4_event_c_video_transcript',
			widget_name: this.getParentWidgetName(),
			video_title: this.root.dataset.title
				? this.root.dataset.title
				: 'No title',
			video_url: this.getVideoURL(),
			video_duration: this.root.dataset.video23
				? 'unknown'
				: this.getVideoLength()
				? this.getVideoLength()
				: 'unknown',
			video_provider: this.getVideoProvider(),
			visible: status,
		});
	},
};
